import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { X, UserCheck, Upload, MapPin, Mail, User as UserIcon, Phone } from 'lucide-react';
import { useToast } from '../ui/Toast';

export const ProfileModal: React.FC = () => {
  const {
    currentUser,
    profileModalOpen,
    setProfileModalOpen,
    completeProfile,
  } = useApp();
  const { showToast } = useToast();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setName(currentUser.name || '');
      setEmail(currentUser.email || '');
      setCity(currentUser.city || '');
      setProfilePicture(currentUser.profile_picture || '');
      setError('');
    }
  }, [profileModalOpen, currentUser]);

  if (!profileModalOpen || !currentUser) return null;

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Full Name is required.');
      return;
    }

    setSubmitting(true);
    const result = await completeProfile(name.trim(), email.trim(), city.trim(), profilePicture.trim());
    setSubmitting(false);

    if (result.success) {
      showToast('Profile updated successfully!', 'success');
      setProfileModalOpen(false);
    } else {
      setError(result.error || 'Failed to update profile.');
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Image file size must be less than 2MB.');
      return;
    }

    const formData = new FormData();
    formData.append('profile_picture', file);

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/upload_profile_picture.php', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.url) {
        setProfilePicture(data.url);
        showToast('Profile picture uploaded successfully!', 'success');
      } else {
        setError(data?.error || 'Failed to upload profile picture.');
      }
    } catch {
      setError('Failed to upload image. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9] animate-in fade-in zoom-in-95 duration-150">
        {/* Header Bar */}
        <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
          <div className="flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-[#1b8e88]" />
            <h3 className="text-base font-bold text-[#111111]">
              My Profile & Account
            </h3>
          </div>
          <button
            onClick={() => setProfileModalOpen(false)}
            className="text-[#5F6368] hover:text-[#111111] p-1 rounded-lg cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2.5 rounded-xl bg-[#FDEDEF] border border-[#D94A5C]/30 text-xs text-[#D94A5C] font-medium">
            {error}
          </div>
        )}

        <form onSubmit={handleProfileSubmit} className="space-y-3">
          {/* Read-Only Mobile Number */}
          <div>
            <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
              <Phone className="w-3.5 h-3.5 text-[#5F6368]" /> Mobile Number (Read-only)
            </label>
            <input
              type="text"
              readOnly
              disabled
              value={currentUser.mobile || 'N/A'}
              className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs bg-[#F0F4F4] text-[#5F6368] font-semibold cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5 text-[#1b8e88]" /> Full Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Rahul Sharma"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
              <Mail className="w-3.5 h-3.5 text-[#1b8e88]" /> Email Address (Optional)
            </label>
            <input
              type="email"
              placeholder="e.g. rahul@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-[#1b8e88]" /> City / Location (Optional)
            </label>
            <input
              type="text"
              placeholder="e.g. Delhi, Mumbai"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
              <Upload className="w-3.5 h-3.5 text-[#1b8e88]" /> Profile Picture (Optional)
            </label>
            <div className="flex items-center gap-3">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt="Preview"
                  className="w-10 h-10 rounded-full object-cover border border-[#56E8E1]"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#F0F4F4] border border-[#E5E9E9] flex items-center justify-center text-xs font-bold text-[#5F6368]">
                  Pic
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="text-xs text-[#5F6368] file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#F0F4F4] file:text-[#111111] hover:file:bg-[#E5E9E9] cursor-pointer"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50 mt-4"
          >
            <UserCheck className="w-4 h-4" />
            {submitting ? 'Saving Changes...' : 'Save Profile Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};
